import React from "react";
import { Link } from "react-router-dom";
import { Phone, MapPin, Mail, Clock, Facebook, Instagram } from "lucide-react";

const SERVICES = [
  { title: "Kitchen Remodeling", slug: "kitchen" },
  { title: "Bathroom Remodeling", slug: "bathroom" },
  { title: "Basement Finishing", slug: "basement" },
  { title: "Whole-House Renovation", slug: "whole-house" },
  { title: "ADU & In-Law Suites", slug: "adu" },
  { title: "Outdoor Living", slug: "outdoor-living" },
  { title: "Historic Restoration", slug: "historic-restoration" },
  { title: "Aging-in-Place", slug: "aging-in-place" },
];

const AREAS = [
  { label: "Dundalk", slug: "dundalk" },
  { label: "Essex", slug: "essex" },
  { label: "Towson", slug: "towson" },
  { label: "Catonsville", slug: "catonsville" },
  { label: "Parkville", slug: "parkville" },
  { label: "Middle River", slug: "middle-river" },
  { label: "Owings Mills", slug: "owings-mills" },
  { label: "Rosedale", slug: "rosedale" },
];

export default function Footer() {
  return (
    <footer className="bg-primary text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <img
              src="https://images.squarespace-cdn.com/content/v1/656a896e54775d222948c688/532ef95c-0298-43a0-938b-bd03a6120663/c4af02ac-7a65-4df3-ae24-b2c387ce173a.png?format=300w"
              alt="Top-Notch Contractors"
              className="h-16 w-auto mb-5 brightness-0 invert"
              width="180"
              height="155"
              loading="lazy"
              decoding="async"
            />
            <div className="space-y-3 font-body">
              <a href="tel:4105989933" className="flex items-center gap-2.5 text-white/75 hover:text-accent transition-colors text-sm">
                <Phone className="w-4 h-4 text-accent flex-shrink-0" />
                (410) 598-9933
              </a>
              <a href="mailto:contact@topnotch-contractors.com" className="flex items-center gap-2.5 text-white/75 hover:text-accent transition-colors text-sm">
                <Mail className="w-4 h-4 text-accent flex-shrink-0" />
                contact@topnotch-contractors.com
              </a>
              <div className="flex items-start gap-2.5 text-white/75 text-sm">
                <MapPin className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                <span>Dundalk, MD — Serving Baltimore &amp; Surrounding Areas</span>
              </div>
              <div className="flex items-start gap-2.5 text-white/75 text-sm">
                <Clock className="w-4 h-4 text-accent flex-shrink-0 mt-0.5" />
                <span>Mon–Fri 7am–5pm</span>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-white/10 text-xs text-white/50 font-body leading-relaxed">
              MHIC# 05-147079 · PA HIC# PA177445<br />
              Licensed &amp; Insured · Since 2010
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="font-heading text-base font-semibold text-white mb-5 tracking-wide">Services</h3>
            <ul className="space-y-2.5">
              {SERVICES.map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="text-sm font-body text-white/70 hover:text-accent transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="font-heading text-base font-semibold text-white mb-5 tracking-wide">Service Areas</h3>
            <ul className="space-y-2.5">
              {AREAS.map((area) => (
                <li key={area.slug}>
                  <Link to={`/areas/${area.slug}`} className="text-sm font-body text-white/70 hover:text-accent transition-colors">
                    {area.label}, MD
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/areas" className="text-sm font-body text-accent hover:text-accent/80 transition-colors font-medium">
                  View all areas →
                </Link>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-heading text-base font-semibold text-white mb-5 tracking-wide">Company</h3>
            <ul className="space-y-2.5">
              {[
                { to: "/about", label: "About Us" },
                { to: "/portfolio", label: "Our Projects" },
                { to: "/blog", label: "Blog" },
                { to: "/contact", label: "Free Consultation" },
                { to: "/areas", label: "Service Areas" },
                { to: "/pay", label: "Pay an Invoice" },
              ].map((link) => (
                <li key={link.to}>
                  <Link to={link.to} className="text-sm font-body text-white/70 hover:text-accent transition-colors">
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-6 flex gap-3">
              <a href="https://www.facebook.com/TopNotchContractors/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-accent transition-colors" aria-label="Facebook">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="https://www.instagram.com/topnotchcontractors/" target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-accent transition-colors" aria-label="Instagram">
                <Instagram className="w-4 h-4" />
              </a>
            </div>
            <div className="mt-5">
              <a
                href="https://www.google.com/maps/place/Top-Notch+Contractors"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-body text-white/60 hover:text-accent transition-colors border border-white/20 rounded px-3 py-1.5"
              >
                ⭐ Read our Google Reviews
              </a>
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs text-white/50 font-body">
            © {new Date().getFullYear()} Top-Notch Contractors. All rights reserved.
          </p>
          <p className="text-xs text-white/50 font-body">
            Baltimore's Trusted Remodeling Contractor Since 2010
          </p>
        </div>
      </div>
    </footer>
  );
}